'use client';

import React, { useState } from 'react';
import { Box, HStack, LucideIcon } from '@skin-seoul/design-system';
import { ProductType, useReviewService, type RecommendedProduct } from '@skin-seoul/service';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import DesktopProductCard from './cards/DesktopProductCard';
import { useProductCompareContext } from '../context/ProductCompareContext';
import RecentlyViewProductCarouselSkeleton from './RecentlyViewProductCarousel.skeleton';
import { trackAddToCartAICompare, trackRecentlyViewedCarousel } from '../utils/analytics';
import { extractPathname } from '@skin-seoul/utils';
interface RecentlyViewProductCarouselProps {
  recentlyViewedProducts: RecommendedProduct[];
}
const RecentlyViewProductCarousel = ({
  recentlyViewedProducts
}: RecentlyViewProductCarouselProps) => {
  const {
    currentProductId,
    setSelectedProductId,
    selectedProductId
  } = useProductCompareContext();
  const initialIndex = React.useMemo(() => {
    const DEFAULT_INDEX = 0;
    if (!currentProductId) {
      return DEFAULT_INDEX;
    }
    const index = recentlyViewedProducts.findIndex(product => (product.parentProductId ?? product.productId) === currentProductId);
    return index !== -1 ? index : DEFAULT_INDEX;
  }, [currentProductId, recentlyViewedProducts]);
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const {
    useGetReviewStatistics
  } = useReviewService();
  const {
    data: reviewStats
  } = useGetReviewStatistics({
    productIds: recentlyViewedProducts.map(product => product.productId).join(',')
  });
  const handleProductClick = (productLink: string) => {
    window.open(productLink, '_blank');
  };
  React.useEffect(() => {
    if (!api) {
      return;
    }
    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    const handleSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      const product = recentlyViewedProducts[selectedIndex];
      setSelectedProductId(prev => ({
        ...prev,
        recentlyViewedProductId: product?.parentProductId ?? product?.productId
      }));
      updateScrollState();
    };
    api.on('select', handleSelect);
    updateScrollState();
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, recentlyViewedProducts, setSelectedProductId]);
  const handlePrevClick = () => {
    api?.scrollPrev();
    trackRecentlyViewedCarousel('left');
  };
  const handleNextClick = () => {
    api?.scrollNext();
    trackRecentlyViewedCarousel('right');
  };
  if (recentlyViewedProducts.length === 0) {
    return null;
  }
  return <Box width="fit-content" display={{
    base: 'none',
    tablet: 'flex'
  }} flexDirection="column" gap={12} data-sentry-element="Box" data-sentry-component="RecentlyViewProductCarousel" data-sentry-source-file="RecentlyViewProductCarousel.tsx">
      <HStack position="relative" width="fit-content" justifyContent="space-between" data-sentry-element="HStack" data-sentry-source-file="RecentlyViewProductCarousel.tsx">
        <LucideIcon color={canScrollPrev ? 'neutral.300' : 'neutral.100'} name="ChevronLeft" boxSize={11} strokeWidth={1} onClick={canScrollPrev ? e => {
        e.preventDefault();
        e.stopPropagation();
        handlePrevClick();
      } : undefined} cursor={canScrollPrev ? 'pointer' : 'not-allowed'} opacity={canScrollPrev ? 1 : 0.5} data-sentry-element="LucideIcon" data-sentry-source-file="RecentlyViewProductCarousel.tsx" />
        <Carousel setApi={setApi} opts={{
        loop: true,
        align: 'start',
        skipSnaps: false,
        startIndex: initialIndex
      }} borderRadius="12px" boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.12)" overflow="hidden" w="256px" data-sentry-element="Carousel" data-sentry-source-file="RecentlyViewProductCarousel.tsx">
          <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="RecentlyViewProductCarousel.tsx">
            {recentlyViewedProducts.map(product => {
            const efficacy = product.efficacy || [];
            const productReviewStats = reviewStats?.statistics.find(stat => stat.productId === product.productId);
            const productId = product.parentProductId ?? product.productId;
            const isDisabled = productId === currentProductId && selectedProductId.recentlyViewedProductId === productId;
            return <Carousel.Item key={product.sku} flexBasis={{
              base: '100%'
            }} minWidth="0" position="relative" backgroundColor="generic.0">
                  <DesktopProductCard sku={product.sku} href={extractPathname(product.productLink)} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} currency={product.currency} originalPrice={product.originalPrice} salePrice={product.salePrice} productType={ProductType.Simple} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} efficacy={efficacy} isDisabled={isDisabled} isCurrent={productId === currentProductId} onTrackAddToCart={() => trackAddToCartAICompare(product)} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (!isDisabled) {
                  handleProductClick(product.productLink);
                }
              }} />
                </Carousel.Item>;
          })}
          </Carousel.Content>
        </Carousel>
        <LucideIcon color={canScrollNext ? 'neutral.300' : 'neutral.100'} name="ChevronRight" boxSize={11} strokeWidth={1} onClick={canScrollNext ? e => {
        e.preventDefault();
        e.stopPropagation();
        handleNextClick();
      } : undefined} cursor={canScrollNext ? 'pointer' : 'not-allowed'} opacity={canScrollNext ? 1 : 0.5} data-sentry-element="LucideIcon" data-sentry-source-file="RecentlyViewProductCarousel.tsx" />
      </HStack>
    </Box>;
};
RecentlyViewProductCarousel.Skeleton = RecentlyViewProductCarouselSkeleton;
export default RecentlyViewProductCarousel;